/* Kolotoč 2.0 hero turntable (sections/kiids-hero-kolotoc.liquid).
   One opening spin, then drag (with inertia), arrows, buttons, dots, horizontal wheel, or "Roztočiť 1 kolo".
   Motion levels come from window.kiidsMotion (assets/kiids-motion.js); without it everything works with no animation. */
if (!customElements.get('kiids-kolotoc')) {
  customElements.define('kiids-kolotoc', class KiidsKolotoc extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.seats = [...this.querySelectorAll('[data-seat]')];
      this.n = this.seats.length;
      if (!this.stage || !this.n) return;
      this.tickets = [...this.querySelectorAll('[data-ticket]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.spinBtn = this.querySelector('[data-spin]');
      this.frontArea = this.querySelector('.kiids-hero__front');
      this.introPending = this.n > 1 && !(window.Shopify && window.Shopify.designMode);
      this.introVisible = !('IntersectionObserver' in window);
      this.stepDeg = 360 / this.n;
      this.angle = 0;
      this.front = 0;
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };

      this.stage.addEventListener('pointerdown', (e) => this.onDown(e), opt);
      this.stage.addEventListener('pointermove', (e) => this.onMove(e), opt);
      this.stage.addEventListener('pointerup', () => this.onUp(), opt);
      this.stage.addEventListener('pointercancel', () => this.onUp(), opt);
      this.stage.addEventListener('wheel', (e) => this.onWheel(e), { signal: this.ac.signal, passive: true });
      this.stage.addEventListener('keydown', (e) => this.onKey(e), opt);
      // Spin pauses only while a mouse is over the turntable. Touch fires pointerenter on every tap, so it never counts.
      this.stage.addEventListener('pointerenter', (e) => { this.hover = e.pointerType === 'mouse'; }, opt);
      this.stage.addEventListener('pointerleave', () => { this.hover = false; }, opt);
      this.addEventListener('click', (e) => this.onClick(e), opt);
      this.addEventListener('pointerdown', (e) => {
        this.introPending = false;
        if (this.introRaf && !e.target.closest('[data-spin]')) this.stopSpin();
      }, opt);
      this.addEventListener('focusin', (e) => {
        if (!e.target.matches(':focus-visible')) return;
        this.introPending = false;
        if (this.introRaf && !e.target.closest('[data-spin]')) this.stopSpin();
      }, opt);
      window.addEventListener('scroll', () => this.onScroll(), { signal: this.ac.signal, passive: true });
      document.addEventListener('kiids:motion', () => this.onMotion(), opt);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { if (this.introRaf) this.stopSpin(); }
        else this.startIntro();
      }, opt);
      this.onMotion();
      if (this.introPending && !this.introVisible) {
        this.introObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
          this.introVisible = true;
          this.introObserver.disconnect();
          this.startIntro();
        }, { threshold: 0.5 });
        this.introObserver.observe(this.stage);
      }
      this.startIntro();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.introObserver) this.introObserver.disconnect();
      this.stopSpin();
      cancelAnimationFrame(this.inertiaRaf);
    }

    fx(name) { return !!(window.kiidsMotion && window.kiidsMotion.on(name)); }
    snap(a) { return Math.round(a / this.stepDeg) * this.stepDeg; }
    frontIdx() { return ((Math.round(this.angle / this.stepDeg) % this.n) + this.n) % this.n; }

    render(dragging) {
      this.stage.style.setProperty('--a', this.angle + 'deg');
      this.stage.classList.toggle('is-dragging', !!dragging);
      const f = this.frontIdx();
      if (f === this.front) return;
      this.front = f;
      this.seats.forEach((s, i) => {
        s.classList.toggle('is-front', i === f);
        const b = s.querySelector('[data-seat-btn]');
        if (b) b.setAttribute('aria-label', i === f ? b.dataset.labelFront : b.dataset.labelTurn);
      });
      this.tickets.forEach((t, i) => { t.hidden = i !== f; });
      this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === f)));
    }

    settle() {
      this.render(false);
      const t = this.tickets[this.front];
      if (!t || !this.fx('inertia')) return;
      t.classList.remove('is-wob');
      void t.offsetWidth;
      t.classList.add('is-wob');
    }

    step(k) { this.angle = this.snap(this.angle) + k * this.stepDeg; this.settle(); }

    goSeat(i) {
      let d = ((i - this.frontIdx()) % this.n + this.n) % this.n;
      if (d > this.n / 2) d -= this.n;
      this.angle = this.snap(this.angle) + d * this.stepDeg;
      this.settle();
    }

    onClick(e) {
      const t = e.target;
      this.introPending = false;
      if (this.introRaf && !t.closest('[data-spin]')) this.stopSpin();
      if (t.closest('[data-prev]')) return this.step(-1);
      if (t.closest('[data-next]')) return this.step(1);
      if (t.closest('[data-spin]')) return this.spinning ? this.stopSpin() : this.startSpin();
      const dot = t.closest('[data-dot]');
      if (dot) return this.goSeat(this.dots.indexOf(dot));
      const seat = t.closest('[data-seat-btn]');
      if (seat) {
        if (this.justDragged) return;
        const i = this.seats.indexOf(seat.closest('[data-seat]'));
        if (i === this.frontIdx()) window.location.href = seat.dataset.url;
        else this.goSeat(i);
      }
    }

    // Drag: 0.32° per px; grabbing during inertia stops on the nearest seat.
    onDown(e) {
      if (e.button !== 0) return;
      this.introPending = false;
      if (this.introRaf) this.stopSpin();
      if (this.spinV) { this.spinV = 0; cancelAnimationFrame(this.inertiaRaf); this.angle = this.snap(this.angle); this.render(false); }
      this.drag = { x: e.clientX, a: this.angle, moved: false, id: e.pointerId, v: 0, lx: e.clientX, lt: performance.now() };
    }

    onMove(e) {
      const d = this.drag;
      if (!d) return;
      const dx = e.clientX - d.x, now = performance.now(), dt = Math.max(1, now - d.lt);
      d.v = d.v * 0.5 + (-(e.clientX - d.lx) * 0.32 / dt) * 0.5;
      d.lx = e.clientX; d.lt = now;
      if (!d.moved && Math.abs(dx) > 6) {
        d.moved = true;
        try { this.stage.setPointerCapture(d.id); } catch (err) {}
      }
      if (d.moved) { this.angle = d.a - dx * 0.32; this.render(true); }
    }

    onUp() {
      const d = this.drag;
      this.drag = null;
      if (!d || !d.moved) return;
      this.justDragged = true;
      setTimeout(() => { this.justDragged = false; }, 80);
      const v = performance.now() - d.lt > 90 ? 0 : d.v;
      if (Math.abs(v) > 0.05 && this.fx('inertia')) this.inertia(v);
      else { this.angle = this.snap(this.angle); this.settle(); }
    }

    // Inertia: speed decays 0.94 per frame, then snaps to the nearest seat.
    inertia(v) {
      let last = performance.now();
      this.spinV = Math.max(-1.5, Math.min(1.5, v));
      const tick = (now) => {
        if (!this.spinV) return;
        const dt = Math.min(48, Math.max(1, now - last));
        last = now;
        this.spinV *= Math.pow(0.94, dt / 16);
        this.angle += this.spinV * dt;
        if (Math.abs(this.spinV) < 0.025 || !this.fx('inertia')) {
          this.spinV = 0;
          this.angle = this.snap(this.angle);
          this.settle();
          return;
        }
        this.render(true);
        this.inertiaRaf = requestAnimationFrame(tick);
      };
      this.inertiaRaf = requestAnimationFrame(tick);
    }

    onWheel(e) {
      const horiz = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const dv = horiz ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      const now = performance.now();
      if (!dv || now - (this.lastWheel || 0) < 380) return;
      this.lastWheel = now;
      this.step(dv > 0 ? 1 : -1);
    }

    onKey(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      else if (e.key === 'Enter' && e.target === this.stage) {
        const b = this.seats[this.frontIdx()].querySelector('[data-seat-btn]');
        if (b) window.location.href = b.dataset.url;
      }
    }

    // Keyboard focus inside the turntable pauses the spin. A clicked button keeps focus without :focus-visible, so it does not.
    keyFocused() {
      const a = document.activeElement;
      return !!a && this.stage.contains(a) && a.matches(':focus-visible');
    }

    startIntro() {
      if (!this.introPending || !this.introVisible || document.hidden) return;
      this.introPending = false;
      if (!this.fx('inertia') || window.kiidsMotion.paused()) return;
      this.spinning = true;
      this.setSpinUi();
      this.stage.classList.add('is-auto-spinning');
      if (this.frontArea) this.frontArea.setAttribute('aria-live', 'off');
      const from = this.angle, target = from + 360 + Math.floor(Math.random() * this.n) * this.stepDeg;
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / 3400);
        this.angle = from + (target - from) * (1 - Math.pow(1 - progress, 3));
        this.render(false);
        if (progress < 1) this.introRaf = requestAnimationFrame(tick);
        else { this.introRaf = null; this.stopSpin(); this.settle(); }
      };
      this.introRaf = requestAnimationFrame(tick);
    }

    // "Roztočiť 1 kolo": one step every spin-ms, pauses while hovered/keyboard-focused, stops after a full round.
    // An explicit click always works; with less motion the steps just jump (no transitions, see kiids-kolotoc.css).
    startSpin() {
      this.stopSpin();
      this.spinning = true;
      this.spinLeft = this.n;
      this.setSpinUi();
      this.timer = setInterval(() => {
        if (this.hover || this.keyFocused() || this.drag || this.spinV) return;
        this.step(1);
        if (--this.spinLeft <= 0) this.stopSpin();
      }, parseInt(this.dataset.spinMs, 10) || 3000);
    }

    stopSpin() {
      clearInterval(this.timer);
      if (this.introRaf) {
        cancelAnimationFrame(this.introRaf);
        this.introRaf = null;
        this.angle = this.snap(this.angle);
        this.render(false);
      }
      if (this.stage) this.stage.classList.remove('is-auto-spinning');
      if (this.frontArea) this.frontArea.setAttribute('aria-live', 'polite');
      this.spinning = false;
      this.setSpinUi();
    }

    setSpinUi() {
      const b = this.spinBtn;
      if (!b) return;
      b.setAttribute('aria-pressed', String(!!this.spinning));
      const l = b.querySelector('[data-spin-label]');
      if (l) l.textContent = this.spinning ? b.dataset.labelPause : b.dataset.labelSpin;
    }

    // Hero exit: turntable tilts back, canopy and stickers drift while the hero scrolls out. No pinning.
    onScroll() {
      if (this.scrollPending || !this.fx('hero')) return;
      this.scrollPending = true;
      requestAnimationFrame(() => {
        this.scrollPending = false;
        const r = this.getBoundingClientRect();
        const p = Math.round(Math.max(0, Math.min(1, -r.top / Math.max(1, r.height))) * 20) / 20;
        if (p !== this.hp) { this.hp = p; this.style.setProperty('--hp', p); }
      });
    }

    onMotion() {
      if (this.spinning && window.kiidsMotion && (window.kiidsMotion.paused() || (this.introRaf && !this.fx('inertia')))) this.stopSpin();
      if (!this.fx('hero')) { this.hp = 0; this.style.setProperty('--hp', 0); }
      else this.onScroll();
    }
  });
}
